import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Share, X } from 'lucide-react';

type Platform = 'ios-safari' | 'android-chrome' | 'desktop-chromium' | 'unsupported';
type DismissedKey = `bz-install-dismissed-${Platform}`;

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

interface NavigatorWithRelated extends Navigator {
  getInstalledRelatedApps?: () => Promise<Array<{ id?: string; platform?: string; url?: string }>>;
  standalone?: boolean;
}

function detectPlatform(): Platform {
  if (typeof window === 'undefined') return 'unsupported';
  const ua = navigator.userAgent;
  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua) && !/(CriOS|FxiOS)/i.test(ua);
  const isChromium = 'BeforeInstallPromptEvent' in window || /Chrome|Edg/i.test(ua);

  if (isIOS) return 'ios-safari';
  if (isAndroid && isChromium) return 'android-chrome';
  if (isChromium) return 'desktop-chromium';
  return 'unsupported';
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  return Boolean((navigator as NavigatorWithRelated).standalone);
}

export function InstallPrompt() {
  const { t } = useTranslation();
  const [platform] = useState<Platform>(detectPlatform);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(`bz-install-dismissed-${platform}` as DismissedKey) === '1';
    } catch {
      return false;
    }
  });
  const [bipEvent, setBipEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [hasNativeApp, setHasNativeApp] = useState<boolean>(false);

  // Hide if running standalone, dismissed, native app installed, or unsupported.
  const visible =
    !dismissed &&
    !isStandalone() &&
    !hasNativeApp &&
    platform !== 'unsupported' &&
    (platform === 'ios-safari' || bipEvent !== null);

  useEffect(() => {
    // Catch the install banner event before Chrome shows it; we trigger it
    // ourselves via a styled CTA.
    const onBip = (e: Event) => {
      e.preventDefault();
      setBipEvent(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onBip);
    window.addEventListener('appinstalled', () => setBipEvent(null));

    // Suppress prompt entirely on Android if the Play Store native app is
    // already installed — `related_applications` in the manifest declares it.
    const nav = navigator as NavigatorWithRelated;
    if (typeof nav.getInstalledRelatedApps === 'function') {
      nav
        .getInstalledRelatedApps()
        .then((apps) => {
          if (apps.some((a) => a.platform === 'play')) setHasNativeApp(true);
        })
        .catch(() => undefined);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(`bz-install-dismissed-${platform}` as DismissedKey, '1');
    } catch {
      /* private mode etc — fine, prompt just shows again next session */
    }
    setDismissed(true);
  };

  const accept = async () => {
    if (bipEvent) {
      try {
        await bipEvent.prompt();
        await bipEvent.userChoice;
      } finally {
        setBipEvent(null);
        dismiss();
      }
    }
  };

  if (!visible) return null;

  return (
    <div
      role="region"
      aria-label={t('install.title', 'Install Bazi Fortune')}
      className="fixed inset-x-3 z-30 rounded-3xl border border-cream-300/60 bg-cream-100/95 p-4 text-ink-800 shadow-card backdrop-blur dark:border-midnight-700/60 dark:bg-midnight-900/95 dark:text-warm-100 md:left-auto md:right-4 md:max-w-sm"
      style={{ bottom: 'max(0.75rem, var(--safe-bottom))' }}
    >
      <div className="flex items-start gap-3">
        <Download className="mt-1 h-5 w-5 shrink-0 text-jade-500" aria-hidden="true" />
        <div className="flex-1">
          <p className="font-serif text-base font-semibold">
            {t('install.title', 'Install Bazi Fortune')}
          </p>
          {platform === 'ios-safari' ? (
            <p className="mt-1 flex flex-wrap items-center gap-1 text-sm text-ink-600 dark:text-warm-300">
              {t('install.iosTap', 'Tap')}
              <Share className="inline h-4 w-4" aria-hidden="true" />
              {t('install.iosThen', 'then "Add to Home Screen"')}
            </p>
          ) : (
            <p className="mt-1 text-sm text-ink-600 dark:text-warm-300">
              {t('install.androidBody', 'Add Bazi Fortune to your home screen for instant access.')}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('common.dismiss', 'Dismiss')}
          className="cursor-pointer rounded-full p-1 text-ink-500 transition-colors duration-200 hover:bg-cream-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jade-500 dark:text-warm-300 dark:hover:bg-midnight-800"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      {bipEvent && platform !== 'ios-safari' && (
        <button
          type="button"
          onClick={() => void accept()}
          className="mt-3 w-full cursor-pointer rounded-2xl bg-jade-500 py-2.5 text-sm font-medium text-white transition-colors duration-200 hover:bg-jade-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jade-500"
        >
          {t('install.cta', 'Install')}
        </button>
      )}
    </div>
  );
}
